import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { createVeyl } from '../services/matching';
import { useLanguage } from '../i18n';

export default function VeylSwitcher({ user, userData, activeVeylId, onSwitch, onNewVeyl }) {
  const { t: translate } = useLanguage();
  const [veyls, setVeyls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewVeylForm, setShowNewVeylForm] = useState(false);
  const [newVeylData, setNewVeylData] = useState({ goalCategory: '', goalDescription: '' });

  const isPremium = userData?.isPremium;
  const maxVeyls = isPremium ? 3 : 1;

  // Must match the category list in OnboardingPage.js and OverviewView.js exactly
  const categories = [
    { key: 'fitnessHealth',           value: 'Fitness & Health' },
    { key: 'learningSkills',          value: 'Learning & Skills' },
    { key: 'businessCareer',          value: 'Business & Career' },
    { key: 'creativeProjects',        value: 'Creative Projects' },
    { key: 'relationshipsConnection', value: 'Relationships & Connection' },
    { key: 'homeLifeOrganization',    value: 'Home & Life Organization' },
    { key: 'moneyFinancialHabits',    value: 'Money & Financial Habits' },
    { key: 'stayingConnected',        value: 'Staying Connected' },
    { key: 'independentLiving',       value: 'Independent Living' },
    { key: 'techConfidence',          value: 'Tech Confidence' },
  ];

  const translateCategory = (categoryValue) => {
    const match = categories.find(c => c.value === categoryValue);
    return match ? translate(match.key) : categoryValue;
  };

  useEffect(() => {
    if (!user) return;
    loadVeyls();
  }, [user, activeVeylId]);

  const loadVeyls = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'veyls'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVeyls(list);
    } catch (err) {
      console.error('Error loading veyls:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVeyl = async () => {
    if (!newVeylData.goalCategory || !newVeylData.goalDescription.trim()) return;
    setCreating(true);
    try {
      const newVeyl = await createVeyl(user.uid, {
        goalCategory: newVeylData.goalCategory,
        goalDescription: newVeylData.goalDescription.trim(),
        timezone: userData.timezone,
        matchPreference: userData.matchPreference || 'anywhere',
      });
      setShowNewVeylForm(false);
      setNewVeylData({ goalCategory: '', goalDescription: '' });
      await loadVeyls();
      if (onNewVeyl) onNewVeyl(newVeyl.id);
    } catch (err) {
      alert(translate('error') + ': ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // Count all active veyls (seeking_partner + matched) toward the limit
  // complete veyls don't count — they're finished
  const activeVeyls = veyls.filter(v => v.status === 'seeking_partner' || v.status === 'matched');
  const canCreateNew = activeVeyls.length < maxVeyls;

  const getStatusLabel = (veyl) => {
    if (veyl.status === 'complete') return { text: translate('completeLabel') + ' ✓', color: '#16a34a' };
    if (veyl.status === 'matched')  return { text: translate('activeLabel'),           color: '#1B7A8A' };
    return                                  { text: translate('seekingLabel') + '...', color: '#f59e0b' };
  };

  if (loading) return null;

  return (
    <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', overflowX: 'auto' }}>

        {veyls.map((veyl, index) => {
          const isActive = veyl.id === activeVeylId;
          const status = getStatusLabel(veyl);
          return (
            <button
              key={veyl.id}
              onClick={() => !isActive && onSwitch(veyl.id)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                padding: '0.5rem 0.875rem', borderRadius: '0.5rem',
                border: isActive ? '2px solid #1B7A8A' : '2px solid #e5e7eb',
                backgroundColor: isActive ? 'rgba(27,122,138,0.08)' : '#FFFFFF',
                cursor: isActive ? 'default' : 'pointer',
                minWidth: '120px', flexShrink: 0, transition: 'all 0.15s'
              }}
            >
              <span style={{ fontSize: '0.75rem', fontWeight: '700', color: isActive ? '#1B7A8A' : '#1A1A1A' }}>
                Veyl {index + 1}
              </span>
              <span style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>
                {translateCategory(veyl.goalCategory)}
              </span>
              <span style={{ fontSize: '0.6rem', color: status.color, fontWeight: '600', marginTop: '0.1rem' }}>
                {status.text}
              </span>
            </button>
          );
        })}

        {/* Show new veyl button only if under the active veyl limit */}
        {canCreateNew && !showNewVeylForm && (
          <button
            onClick={() => setShowNewVeylForm(true)}
            style={{
              padding: '0.5rem 0.875rem', borderRadius: '0.5rem',
              border: '2px dashed #e5e7eb', backgroundColor: '#FFFFFF',
              cursor: 'pointer', color: '#6b7280', fontSize: '0.8rem',
              fontWeight: '600', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.25rem'
            }}
          >
            {translate('newVeyl')}
          </button>
        )}

        {/* Show upgrade prompt only when active veyl limit is reached and not premium */}
        {!canCreateNew && !isPremium && (
          <div style={{ fontSize: '0.7rem', color: '#4A1D5E', fontWeight: '500', flexShrink: 0, padding: '0.5rem' }}>
            {translate('upgradeForMore')}
          </div>
        )}
      </div>

      {/* New Veyl form */}
      {showNewVeylForm && (
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '1rem', borderTop: '1px solid #e5e7eb', backgroundColor: '#FFFFFF' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontWeight: '700', color: '#1A1A1A', fontSize: '0.95rem' }}>{translate('newVeylTitle')}</h3>
            <button onClick={() => setShowNewVeylForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.1rem' }}>✕</button>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#1A1A1A', marginBottom: '0.5rem' }}>
              {translate('goalCategoryLabel')}
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setNewVeylData(prev => ({ ...prev, goalCategory: cat.value }))}
                  style={{
                    padding: '0.4rem 0.75rem', borderRadius: '999px',
                    border: newVeylData.goalCategory === cat.value ? '2px solid #1B7A8A' : '2px solid #e5e7eb',
                    backgroundColor: newVeylData.goalCategory === cat.value ? 'rgba(27,122,138,0.08)' : '#FFFFFF',
                    color: newVeylData.goalCategory === cat.value ? '#1B7A8A' : '#6b7280',
                    fontWeight: newVeylData.goalCategory === cat.value ? '700' : '400',
                    cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  {translate(cat.key)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#1A1A1A', marginBottom: '0.5rem' }}>
              {translate('goalDescriptionLabel')}
            </label>
            <textarea
              value={newVeylData.goalDescription}
              onChange={(e) => setNewVeylData(prev => ({ ...prev, goalDescription: e.target.value }))}
              placeholder={translate('goalPlaceholder')}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', height: '4rem', fontFamily: 'inherit', fontSize: '0.875rem', resize: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowNewVeylForm(false)}
              style={{ padding: '0.6rem 1.25rem', backgroundColor: '#F5F5F5', color: '#1A1A1A', border: '1px solid #e5e7eb', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              {translate('cancel')}
            </button>
            <button
              onClick={handleCreateVeyl}
              disabled={creating || !newVeylData.goalCategory || !newVeylData.goalDescription.trim()}
              style={{ padding: '0.6rem 1.25rem', backgroundColor: '#1B7A8A', color: '#FFFFFF', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.6 : 1, fontSize: '0.875rem' }}
            >
              {creating ? translate('starting') : translate('startVeyl')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}